import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Shield, ArrowLeft } from 'lucide-react';
import { Navbar } from './Navbar';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Full-screen prompt shown to unauthenticated users trying to access protected features.
 * Replaces generic "Not Found" or login redirects with a friendly signup invitation.
 */
export const LoginPromptScreen = ({ feature, showNavbar = true, showBack = true }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const featureLabel = feature || t('loginPrompt.defaultFeature') || 'denne funktion';

  return (
    <div className="min-h-screen">
      {showNavbar && <Navbar />}
      <div className="flex items-center justify-center px-4" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="max-w-md w-full text-center space-y-6">
          {/* Icon */}
          <div className="mx-auto w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Shield className="w-10 h-10 text-emerald-400" />
          </div>

          {/* Heading */}
          <div>
            <h2 className="font-heading text-2xl text-white mb-2" data-testid="login-prompt-title">
              {t('loginPrompt.title') || 'Opret en gratis profil'}
            </h2>
            <p className="text-zinc-400 text-sm leading-relaxed" data-testid="login-prompt-description">
              {t('loginPrompt.description') || `For at få adgang til ${featureLabel} skal du oprette en gratis profil eller logge ind.`}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/register')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-sm transition-all active:scale-[0.98]"
              data-testid="login-prompt-register-btn"
            >
              <UserPlus className="w-4 h-4" />
              {t('loginPrompt.createAccount') || 'Opret gratis profil'}
            </button>

            <button
              onClick={() => navigate('/login')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-sm transition-all border border-white/10"
              data-testid="login-prompt-login-btn"
            >
              <LogIn className="w-4 h-4" />
              {t('loginPrompt.login') || 'Jeg har allerede en profil'}
            </button>
          </div>

          {/* Back link */}
          {showBack && (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
              data-testid="login-prompt-back-btn"
            >
              <ArrowLeft className="w-3 h-3" />
              {t('common.goBack') || 'Gå tilbage'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPromptScreen;
